import type { DoseWarning } from '@rx/shared';

/**
 * The over-dose soft warning.
 *
 * It asks a question; it does not refuse. "Yes, this is extra" records a genuine extra
 * dose, which is precisely the signal Richard's doctors are missing today. Blocking the
 * entry would only mean the dose still happened and nobody wrote it down.
 *
 * The tone matters as much as the logic. Someone who feels caught out stops logging, and
 * a log nobody keeps is worth nothing.
 */
export function OverdoseSheet({
  warning,
  busy,
  onConfirm,
  onCancel,
}: {
  warning: DoseWarning;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true" aria-label={warning.title}>
      <div className="sheet">
        <h2 className="sheet-title">{warning.title}</h2>
        <p className="sheet-body">{warning.body}</p>

        {/*
          Shown whenever the limit came from generic labeling rather than his own
          prescription. Presenting a guessed limit as a doctor's instruction would be
          both wrong and, for ondansetron, actively misleading — nobody knows his real
          frequency.
        */}
        {warning.advisoryNote !== undefined && (
          <p className="sheet-advisory">{warning.advisoryNote}</p>
        )}

        <button className="btn-primary" disabled={busy} onClick={onConfirm}>
          {busy ? 'Saving…' : 'Yes, this is an extra dose'}
        </button>
        <button className="btn-quiet sheet-cancel" disabled={busy} onClick={onCancel}>
          No, I made a mistake
        </button>
      </div>
    </div>
  );
}
